import {
  DealPayrollAssignmentTraining, DealPayrollGradeCode, PAYROLL_CALCULATION_METHOD, PayrollCalculationMethod,
  UserPayrollSettingsResponse
} from 'app/models/deal-payroll.model';
import {
  DealPayrollPositionOption, dealPayrollPositionOptions
} from 'app/models/deal-payroll-settings.adapter';
import {dealPayrollBlockerKeys} from 'app/models/deal-payroll-presentation';
import {
  DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY, DEAL_PAYROLL_USER_SETTINGS_FEEDBACK_KEY
} from './deal-payroll-user-settings.constants';
import {
  canStartDealPayrollAssignment, currentDealPayrollPositionKey,
  dealPayrollProbationBlockers, enabledDealPayrollGrades,
  hasDealPayrollAssignmentOptions, incompleteAssignmentTrainings, isDealPayrollMethodSelected
} from './deal-payroll-user-settings.selectors';
import {
  DealPayrollGradeOption,
  DealPayrollUserSettingsViewState
} from './deal-payroll-user-settings.types';

export class DealPayrollUserSettingsState implements DealPayrollUserSettingsViewState {
  settings: UserPayrollSettingsResponse = null;
  positionOptions: DealPayrollPositionOption[] = [];
  gradeOptions: DealPayrollGradeOption[] = [];
  blockerKeys: string[] = [];
  selectedPositionKey: string = null;
  selectedGradeCode: DealPayrollGradeCode = null;
  dealSelected = false;
  loading = false;
  saving = false;
  loaded = false;
  assignmentRequired = false;
  positionRequired = false;
  gradeRequired = false;
  errorKey = DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.NONE;
  pendingMethod: PayrollCalculationMethod = null;
  saveConfirmed = false;
  editingLocked = false;
  requiresReload = false;

  resetForUserChange(): void {
    this.clearSettings();
    this.resetRequestState();
    this.resetEditor();
    this.clearValidation();
  }

  startLoading(preserveError: boolean): void {
    this.loading = true;
    this.loaded = !!this.settings;
    this.saveConfirmed = false;
    if (!preserveError) {
      this.clearError();
    }
  }

  useSettings(response: UserPayrollSettingsResponse): void {
    this.requiresReload = false;
    this.settings = response;
    this.positionOptions = dealPayrollPositionOptions(response);
    this.gradeOptions = enabledDealPayrollGrades(response);
    this.assignmentRequired = hasDealPayrollAssignmentOptions(response);
    this.selectedPositionKey = currentDealPayrollPositionKey(response);
    this.selectedGradeCode = response.gradeCode;
    this.dealSelected = response.payrollCalculationMethod
      === PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL;
    this.pendingMethod = null;
    this.blockerKeys = dealPayrollBlockerKeys(response.blockers);
    this.applyManagementAccess(response.canManage);
    this.finishLoading();
  }

  rejectLoad(errorKey: string, blockerCodes: string[]): void {
    this.finishRejectedRequest();
    this.requiresReload = true;
    this.errorKey = errorKey;
    this.blockerKeys = dealPayrollBlockerKeys(blockerCodes);
  }

  rejectInvalidLoad(): void {
    this.finishRejectedRequest();
    this.requiresReload = true;
    this.errorKey = DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.LOAD;
    this.blockerKeys = [];
  }

  startSaving(method: PayrollCalculationMethod): void {
    this.saving = true;
    this.saveConfirmed = false;
    this.clearError();
    this.pendingMethod = method;
  }

  finishSaved(response: UserPayrollSettingsResponse): void {
    this.clearError();
    this.useSettings(response);
    this.saveConfirmed = !response.pendingOperationToken;
  }

  rejectSavedResponse(): void {
    this.saving = false;
    this.saveConfirmed = false;
    this.requiresReload = true;
    this.errorKey = DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.SAVE;
  }

  prepareSaveError(blockerCodes: string[]): void {
    this.saving = false;
    this.saveConfirmed = false;
    this.blockerKeys = dealPayrollBlockerKeys(blockerCodes);
  }

  rejectUserRevisionConflict(): void {
    this.requiresReload = true;
    this.editingLocked = true;
    this.errorKey = DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.USER_STALE;
  }

  finishSaveError(errorKey: string, editingLocked: boolean): void {
    this.requiresReload = true;
    this.editingLocked = this.editingLocked || editingLocked;
    this.errorKey = errorKey;
  }

  clearError(): void {
    this.errorKey = DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.NONE;
  }

  beginMethodSelection(method: PayrollCalculationMethod): void {
    this.pendingMethod = method;
    this.saveConfirmed = false;
    this.clearError();
  }

  beginDealSelection(): void {
    this.beginMethodSelection(PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL);
    this.dealSelected = true;
    this.clearValidation();
  }

  selectPosition(positionKey: string): void {
    this.selectedPositionKey = positionKey;
    this.beginDealEdit();
  }

  selectGrade(gradeCode: DealPayrollGradeCode): void {
    this.selectedGradeCode = gradeCode;
    this.gradeRequired = false;
  }

  resetDealDraft(): void {
    this.dealSelected = false;
    this.pendingMethod = null;
    this.restoreConfirmedAssignment();
    this.clearValidation();
  }

  showValidation(): void {
    this.positionRequired = false;
    this.gradeRequired = this.assignmentRequired && !this.selectedGradeCode;
  }

  get canManageSettings(): boolean {
    return !!this.settings && this.settings.canManage && !this.editingLocked && !this.requiresReload
      && !this.settings.pendingOperationToken && !this.loading && !this.saving;
  }

  get canResume(): boolean {
    return !!this.settings && !!this.settings.pendingOperationToken && this.settings.canManage
      && !this.editingLocked && !this.requiresReload && !this.loading && !this.saving;
  }

  get canStartDealAssignment(): boolean {
    return canStartDealPayrollAssignment(this.settings);
  }

  get canEnableDeal(): boolean { return !!this.settings && this.settings.canEnableDeal !== false; }

  get dealEnableBlockers(): string[] {
    return dealPayrollBlockerKeys(this.settings && this.settings.dealEnableBlockers || []);
  }

  get probationBlockers(): string[] {
    return dealPayrollProbationBlockers(this.settings);
  }

  get probationPermissionRequired(): boolean {
    const eligibility = this.settings && this.settings.assignmentEligibility;
    return !this.canStartDealAssignment && !!eligibility && !eligibility.canAssign
      && !this.probationBlockers.length;
  }

  get probationUnavailable(): boolean {
    return !!this.settings && !this.canStartDealAssignment && !this.settings.assignmentEligibility;
  }

  get incompleteTrainings(): DealPayrollAssignmentTraining[] {
    return incompleteAssignmentTrainings(this.settings);
  }

  get feedbackKey(): string {
    if (this.loading) {
      return DEAL_PAYROLL_USER_SETTINGS_FEEDBACK_KEY.LOADING;
    }
    if (this.saving) {
      return DEAL_PAYROLL_USER_SETTINGS_FEEDBACK_KEY.SAVING;
    }
    return this.saveConfirmed
      ? DEAL_PAYROLL_USER_SETTINGS_FEEDBACK_KEY.SAVED
      : DEAL_PAYROLL_USER_SETTINGS_FEEDBACK_KEY.NONE;
  }

  isPayrollMethodSelected(method: PayrollCalculationMethod): boolean {
    return isDealPayrollMethodSelected(this.settings, method);
  }

  isPayrollMethodPending(method: PayrollCalculationMethod): boolean {
    return this.pendingMethod === method && !this.isPayrollMethodSelected(method);
  }

  private resetRequestState(): void {
    this.loading = false;
    this.saving = false;
    this.loaded = false;
    this.saveConfirmed = false;
    this.editingLocked = false;
    this.requiresReload = false;
    this.clearError();
  }

  private resetEditor(): void {
    this.assignmentRequired = false;
    this.pendingMethod = null;
    this.selectedPositionKey = null;
    this.selectedGradeCode = null;
    this.dealSelected = false;
  }

  private clearSettings(): void {
    this.settings = null;
    this.positionOptions = [];
    this.gradeOptions = [];
    this.blockerKeys = [];
  }

  private clearValidation(): void {
    this.positionRequired = false;
    this.gradeRequired = false;
  }

  private beginDealEdit(): void {
    this.pendingMethod = PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL;
    this.saveConfirmed = false;
    this.clearValidation();
  }

  private restoreConfirmedAssignment(): void {
    this.selectedPositionKey = this.settings
      ? currentDealPayrollPositionKey(this.settings) : null;
    this.selectedGradeCode = this.settings ? this.settings.gradeCode : null;
  }

  private applyManagementAccess(canManage: boolean): void {
    this.editingLocked = canManage !== true;
    if (this.editingLocked) {
      this.errorKey = DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.FORBIDDEN;
    }
  }

  private finishLoading(): void {
    this.loading = false;
    this.saving = false;
    this.loaded = true;
    this.clearValidation();
  }

  private finishRejectedRequest(): void {
    this.loading = false;
    this.loaded = !!this.settings;
    this.saveConfirmed = false;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/deal-payroll-user-settings/deal-payroll-user-settings.state.ts